<div class="ref-section">
  <div class="ref-section__card" [class.ref-section__card--disabled]="contentDisabled" [class.ref-section__card--picker]="pickerOpen">
    @if (pickerOpen) {
      <div class="ref-section__picker-head">
        <ng-container *ngTemplateOutlet="sectionTitleTpl" />
        <span class="ref-section__spacer"></span>
        <button
          type="button"
          class="ref-section__close-btn"
          (click)="closePicker()"
          [attr.aria-label]="'tenders.responses.documents.remove-all' | translate"
        >
          <i class="ph ph-x" aria-hidden="true"></i>
        </button>
      </div>
      <div class="ref-section__picker-body">
        <app-reference-document-picker
          [subtypeOptions]="subtypeOptions"
          [existingCount]="fileRows.length"
          [attachedByLibraryId]="attachedByLibraryId"
          [initialTab]="pickerInitialTab"
          (confirm)="onPickerConfirm($event)"
          (cancel)="closePicker()"
        />
      </div>
    } @else {
      <div class="ref-section__panel-head">
        <button
          type="button"
          class="ref-section__panel-toggle"
          [class.ref-section__panel-toggle--static]="!collapsible"
          (click)="togglePanelExpanded()"
          [attr.aria-expanded]="expanded"
        >
          <ng-container *ngTemplateOutlet="sectionTitleTpl" />
          <span class="ref-section__spacer"></span>
          @if (!expanded && processingStep === DocumentProcessingStepEnum.UPLOADING && uploadBatchTotal > 0) {
            <ex-pill variant="info" [showSpinner]="true">
              {{ 'tenders.responses.documents.upload-loading' | translate: { completed: uploadBatchCompleted, total: uploadBatchTotal } }}
            </ex-pill>
          } @else if (!expanded && processingStep === DocumentProcessingStepEnum.CATEGORIZING) {
            <ex-pill variant="warning" [showSpinner]="true">
              {{
                'tenders.responses.documents.categorization-loading'
                  | translate: { completed: categorizationCompleted, total: categorizationTotal }
              }}
            </ex-pill>
          } @else if (!expanded && uncategorizedCount > 0) {
            <ex-pill variant="warning" phosphorIcon="warning">
              {{ 'tenders.responses.documents.to-categorize-count' | translate: { count: uncategorizedCount } }}
            </ex-pill>
          } @else if (showAutoSelection) {
            <span class="ref-section__auto-badge">
              <i class="ph-fill ph-sparkle" aria-hidden="true"></i>
              {{ 'tenders.responses.documents.auto-selection.badge-header' | translate }}
            </span>
          }
          @if (collapsible) {
            <i class="ph ph-caret-down ref-section__caret" [class.ref-section__caret--expanded]="expanded" aria-hidden="true"></i>
          }
        </button>
      </div>

      @if (!expanded && isProcessing) {
        <div
          class="ref-section__batch-bar"
          role="progressbar"
          [attr.aria-valuemin]="0"
          [attr.aria-valuemax]="100"
          [attr.aria-valuenow]="importCardProgressPercent"
        >
          <div
            class="ref-section__batch-bar-fill"
            [class.ref-section__batch-bar-fill--warning]="processingStep === DocumentProcessingStepEnum.CATEGORIZING"
            [style.width.%]="importCardProgressPercent"
          ></div>
        </div>
      }

      <div class="ref-section__expandable" [class.ref-section__expandable--collapsed]="!expanded">
        <div class="ref-section__inner">
          @if (libraryCountLoaded) {
            @if (showOnboarding) {
              <app-library-onboarding-card
                [variant]="libraryEmpty ? 'empty' : 'low'"
                [disabled]="contentDisabled"
                (goToLibrary)="goToLibrary()"
                (later)="onLaterClicked()"
              />
            } @else if (libraryNeedsAttention) {
              <app-library-health-banner [variant]="libraryEmpty ? 'empty' : 'low'" (goToLibrary)="goToLibrary()" />
            } @else {
              <app-auto-selection-card (goToLibrary)="goToLibrary()" />
            }
          }

          @if (!showOnboarding) {
            <div class="ref-section__advanced">
              @if (advancedAlwaysOpen) {
                <p class="ref-section__advanced-label">{{ 'tenders.responses.documents.advanced.label' | translate }}</p>
              } @else {
                <button type="button" class="ref-section__advanced-trigger" (click)="toggleAdvanced()" [attr.aria-expanded]="advancedOpen">
                  <i class="ph ph-faders-horizontal" aria-hidden="true"></i>
                  {{ 'tenders.responses.documents.advanced.trigger' | translate }}
                  <i class="ph ph-caret-down ref-section__caret" [class.ref-section__caret--expanded]="advancedOpen" aria-hidden="true"></i>
                </button>
              }

              @if (advancedAlwaysOpen || advancedOpen) {
                @if (ignoredFiles.length > 0) {
                  <app-ignored-files-notice [files]="ignoredFiles" />
                }

                @if (duplicates.length) {
                  <app-duplicate-resolver surface="project" [items]="duplicates" (resolve)="onDuplicateResolve($event)" />
                }

                @if (uncategorizedCount > 0 && !isProcessing) {
                  <app-uncategorized-notice [count]="uncategorizedCount" />
                }

                <div class="ref-section__cards">
                  @if (libraryDocuments.length > 0) {
                    <div class="ref-card">
                      <button type="button" class="ref-card__head" (click)="toggleLibraryCard()" [attr.aria-expanded]="libraryCardOpen">
                        <span class="ref-card__chip ref-card__chip--success" aria-hidden="true">
                          <i class="ph ph-books"></i>
                        </span>
                        <span class="ref-card__title">
                          {{
                            (libraryDocuments.length === 1
                              ? 'tenders.responses.documents.advanced.library-card-one'
                              : 'tenders.responses.documents.advanced.library-card-many'
                            ) | translate: { count: libraryDocuments.length }
                          }}
                        </span>
                        <i class="ph ph-caret-down ref-card__caret" [class.ref-card__caret--open]="libraryCardOpen" aria-hidden="true"></i>
                      </button>
                      @if (libraryCardOpen) {
                        <div class="ref-card__body">
                          <div class="ref-card__list">
                            @for (file of libraryDocuments; track file.uid) {
                              <ng-container *ngTemplateOutlet="rowTpl; context: { $implicit: file }" />
                            }
                          </div>
                          <div class="ref-card__footer">
                            <button type="button" class="ref-card__footer-btn" [disabled]="contentDisabled" (click)="openLibraryPicker()">
                              <i class="ph ph-books" aria-hidden="true"></i>
                              {{ 'tenders.responses.documents.advanced.modify-library-selection' | translate }}
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                  } @else {
                    <app-ref-access-bar
                      kind="library"
                      [frozen]="contentDisabled"
                      [disabled]="libraryEmpty"
                      [tooltip]="libraryEmpty ? ('tenders.responses.documents.advanced.library-bar-empty-tooltip' | translate) : null"
                      (open)="openLibraryPicker()"
                    />
                  }

                  @if (showImportCard) {
                    <div class="ref-card">
                      <button type="button" class="ref-card__head" (click)="toggleImportCard()" [attr.aria-expanded]="importCardOpen">
                        <span
                          class="ref-card__chip"
                          [class.ref-card__chip--success]="!isProcessing"
                          [class.ref-card__chip--busy]="isProcessing"
                          aria-hidden="true"
                        >
                          @if (isProcessing) {
                            <span
                              class="ref-card__spinner"
                              [class.ref-card__spinner--warning]="processingStep === DocumentProcessingStepEnum.CATEGORIZING"
                            ></span>
                          } @else {
                            <i class="ph ph-check"></i>
                          }
                        </span>
                        <span class="ref-card__head-main">
                          <span class="ref-card__title">
                            @if (isProcessing) {
                              {{
                                (processingStep === DocumentProcessingStepEnum.CATEGORIZING
                                  ? 'tenders.responses.documents.advanced.import-card-categorizing'
                                  : 'tenders.responses.documents.advanced.import-card-uploading'
                                ) | translate: { completed: importCardProgressCompleted, total: importCardProgressTotal }
                              }}
                            } @else {
                              {{
                                (importedDocuments.length === 1
                                  ? 'tenders.responses.documents.advanced.import-card-one'
                                  : 'tenders.responses.documents.advanced.import-card-many'
                                ) | translate: { count: importedDocuments.length }
                              }}
                            }
                          </span>
                          @if (isProcessing) {
                            <span class="ref-card__progress">
                              <span
                                class="ref-card__progress-fill"
                                [class.ref-card__progress-fill--warning]="processingStep === DocumentProcessingStepEnum.CATEGORIZING"
                                [style.width.%]="importCardProgressPercent"
                              ></span>
                            </span>
                          }
                        </span>
                        <i class="ph ph-caret-down ref-card__caret" [class.ref-card__caret--open]="importCardOpen" aria-hidden="true"></i>
                      </button>
                      @if (importCardOpen) {
                        <div class="ref-card__body">
                          @if (showImportTip) {
                            <app-import-tip (dismiss)="dismissImportTip()" />
                          }
                          <div class="ref-card__list">
                            @for (file of importedDocuments; track file.uid) {
                              <ng-container *ngTemplateOutlet="rowTpl; context: { $implicit: file }" />
                            }
                          </div>
                          <div class="ref-card__footer">
                            <app-document-import-zone
                              variant="compact"
                              [addLabel]="'tenders.responses.documents.picker.import-more' | translate"
                              [disabled]="contentDisabled"
                              (filesSelected)="onImportZoneFiles($event)"
                              (filesRejected)="onImportZoneRejected($event)"
                            />
                          </div>
                        </div>
                      }
                    </div>
                  } @else {
                    <app-ref-access-bar kind="import" [frozen]="contentDisabled" (open)="openImportPicker()" />
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>

<ng-template #sectionTitleTpl>
  <div class="ref-section__card-header-title">
    <i class="ph ph-folder"></i>
    <p>{{ title }}</p>
    @if (actionRequired) {
      <ex-pill variant="warning">{{ 'tenders.responses.documents.action-required' | translate }}</ex-pill>
    } @else {
      <ex-pill variant="neutral">{{ 'tenders.responses.documents.optional' | translate }}</ex-pill>
    }
  </div>
</ng-template>

<ng-template #rowTpl let-file>
  <div
    class="ref-row"
    [class.ref-row--confirm]="pushConfirm?.uid === file.uid"
    [class.ref-row--categorizing]="file.processingStep === DocumentProcessingStepEnum.CATEGORIZING"
    [class.ref-row--uploading]="file.processingStep === DocumentProcessingStepEnum.UPLOADING"
    [attr.data-file-id]="file.uid"
  >
    <div class="ref-row__main">
      <app-document-identity
        class="ref-row__identity"
        [name]="file.name"
        [subtypeName]="subtypeNameOf(file)"
        [fileSize]="file.size"
        [processingStep]="file.processingStep ?? null"
        [uploadProgress]="file.uploadProgress ?? null"
        [isArchive]="isArchiveFile(file)"
        [justCategorized]="!!file.justCategorized"
        [showUncategorized]="file.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED"
        [inLibraryLabel]="file.isInLibrary ? ('tenders.responses.documents.in-library' | translate) : null"
        [addToLibraryLabel]="
          !file.isInLibrary &&
          pushConfirm?.uid !== file.uid &&
          (file.processingStep === DocumentProcessingStepEnum.CATEGORIZED ||
            file.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED)
            ? ('tenders.responses.documents.add-to-library' | translate)
            : null
        "
        [libraryChecking]="libraryCheckingUid === file.uid"
        [libraryCheckingLabel]="'tenders.responses.documents.checking-library' | translate"
        (libraryAction)="onLibraryAction(file)"
      />

      <div class="ref-row__actions">
        @if (
          (file.processingStep === DocumentProcessingStepEnum.CATEGORIZED ||
            file.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED) &&
          subtypeOptions.length
        ) {
          <app-category-selector
            [options]="subtypeOptions"
            [value]="getSubTypeDisplayName(file) || null"
            [uncategorized]="file.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED && !file.subtype"
            (optionSelected)="selectSubtype(file, $event)"
            (customSelected)="selectCustomSubtype(file, $event)"
          />
        }

        @if (pushConfirm?.uid !== file.uid && file.processingStep !== DocumentProcessingStepEnum.UPLOADING) {
          <button
            type="button"
            class="ref-row__remove"
            (click)="onRemove(file)"
            [attr.aria-label]="'tenders.responses.documents.remove-from-project' | translate"
          >
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
        }
      </div>
    </div>

    @if (pushConfirm?.uid === file.uid) {
      <div class="ref-row__push">
        <p class="ref-row__push-title">
          {{ 'tenders.responses.documents.dedup.push-title' | translate }}
        </p>
        <p
          class="ref-row__push-subtitle"
          [innerHTML]="
            pushConfirm!.projects.length === 0
              ? ('tenders.responses.documents.push-impact-library-only' | translate)
              : pushConfirm!.projects.length === 1
                ? ('tenders.responses.documents.push-impact-one' | translate)
                : ('tenders.responses.documents.push-impact-many' | translate: { count: pushConfirm!.projects.length })
          "
        ></p>
        @if (pushConfirm!.projects.length) {
          <div class="ref-row__push-projects" [class.ref-row__push-projects--expanded]="pushConfirm!.expanded">
            @for (project of pushProjectsPreview; track project.id) {
              <a
                class="ref-row__project-pill"
                [class.ref-row__project-pill--closed]="project.is_active === false"
                [href]="'/tenders/responses/' + project.id"
                target="_blank"
                rel="noopener noreferrer"
                [attr.title]="
                  project.is_active === false
                    ? ('tenders.library.delete-modal.project-closed' | translate)
                    : ('tenders.library.delete-modal.open-project' | translate)
                "
                (click)="$event.stopPropagation()"
              >
                <span class="ref-row__project-pill-dot" aria-hidden="true"></span>
                <span class="ref-row__project-pill-name">{{ project.name }}</span>
                <i class="ph ph-arrow-square-out ref-row__project-pill-icon" aria-hidden="true"></i>
              </a>
            }
            @if (!pushConfirm!.expanded && pushProjectsHiddenCount > 0) {
              <button type="button" class="ref-row__push-more" (click)="togglePushProjectsExpanded()">
                {{
                  (pushProjectsHiddenCount === 1
                    ? 'tenders.responses.documents.push-show-more'
                    : 'tenders.responses.documents.push-show-more-many'
                  ) | translate: { count: pushProjectsHiddenCount }
                }}
              </button>
            }
          </div>
          @if (pushConfirm!.expanded && pushConfirm!.projects.length > 2) {
            <button type="button" class="ref-row__push-more" (click)="togglePushProjectsExpanded()">
              {{ 'tenders.responses.documents.push-reduce' | translate }}
            </button>
          }
        }
        <div class="ref-row__push-actions">
          <button type="button" class="ref-row__push-cancel" (click)="cancelPushConfirm()">
            {{ 'tenders.responses.documents.dedup.cancel' | translate }}
          </button>
          <button type="button" class="ref-row__push-replace" (click)="confirmPushReplace(file)">
            {{ 'tenders.responses.documents.dedup.replace' | translate }}
          </button>
        </div>
      </div>
    }
  </div>
</ng-template>
